import {defineStore} from 'pinia'
import router from '@/router'

import {useDashboardStore} from "@/stores/dashboard";
import {useUserStore} from "@/stores/user";
import {useProfileStore} from "@/stores/profile";
import {useEventsStore} from "@/stores/events";
import {useApplicationsStore} from "@/stores/applications";
import {useAssignmentsStore} from "@/stores/assignments";
import {useRatingStore} from "@/stores/ratings";
import {useRecommendationStore} from "@/stores/recommendations";

import authAPI from '@/api/auth'
import TokenService from "@/services/token-service";

export const useAuthStore = defineStore('auth_store', {
    state: () => ({
        authenticated: false
    }),
    //const user = useSessionStorage<string | null>('__webai_auth_session__', null)
    getters: {
        // const isAuthenticated = () => user.value != null
        //isAuthenticated: (state) => !!useUserStore().currentUser
        isAuthenticated: (state) => state.authenticated,
    },
    actions: {
        /**
         * Fetch current user via `authAPI`
         * Important: There is also a `fetchCurrentUser` action in `userStore` but
         * this one is responsible for fetching the user info from the backend.
         *
         * - If token is not available, do nothing
         * - If token is available, fetch user info
         * - If user info is not available, logout
         * - If user info is available, store it in `userStore`
         */
        async fetchCurrentUser(force: boolean = false) {
            const token = TokenService.getLocalAccessToken();
            if (!token) return;

            try {
                this.authenticated = true;
                await useUserStore().fetchCurrentUser(force); // 📦 Let userStore own the data

            } catch (error) {
                log.error("❌ Failed to fetch user:", error);
                await this.logout();
            }
        },
        /**
         * ✅ Register via `authAPI`
         */
        async register(data: {type: string, organisation?: string, name: string, email: string, password: string}) {
            log.debug("authStore register", data);
            try {
                return await authAPI.register(data);
            } catch (error) {
                log.error("❌ Login failed:", error);
                throw error;
            }
        },
        /**
         * ✅ Login via `authAPI`
         */
        async login(credentials: {email: string, password: string}) {
            try {
                const response = await authAPI.login(credentials);
                // Store user session (TokenService is handled inside `authAPI`)
                await this.fetchCurrentUser(true); // force fresh fetch
                return response;
            } catch (error) {
                log.error("❌ Login failed:", error);
                throw error;
            }
        },
        /**
         * ✅ Logout via `authAPI`
         */
        async logout() {
            try {
                await authAPI.logout();
                // user.value = null;
                //router.push("/auth/sign-in");
            } catch (error) {
                log.error("❌ Logout failed:", error);
            } finally {
                this.authenticated = false;
                resetAllStores(); // ✅ Reset all other stores
                router.push("/");
            }
        },
        /**
         * ✅ Forgot password via `authAPI`
         * @param data
         */
        async forgotPassword(data: {email: string}) {
            try {
                log.debug("authStore forgotPassword data=", data);
                return await authAPI.forgotPassword(data)
            } catch (error) {
                log.error("❌ Login failed:", error);
                throw error;
            }
        },
        /**
         * ✅ Reset password via `authAPI`
         * @param data
         */
        async resetPassword(data: {password: string, resetPasswordToken: string}) {
            try {
                log.debug("authStore resetPassword data=", data);
                return await authAPI.resetPassword(data)
            } catch (error) {
                log.error("❌ Login failed:", error);
                throw error;
            }
        },
        /**
         * ✅ Change password via `authAPI`
         * This is used to change the password of the current user.
         * It requires the current password, new password and confirmation of the new password.
         * @param data
         */
        async changePassword(data: {currentPassword: string, newPassword: string, newPasswordConfirm: string}) {
            try {
                log.debug("authStore changePassword data=", data);
                return await authAPI.changePassword(data)
            } catch (error) {
                log.error("❌ Login failed:", error);
                throw error;
            }
        },

        /**
         * ✅ Refresh token via `authAPI`
         * @param payload
         */
        async refresh(payload: {email: string, refreshToken: string}) {
            try {
                const response = await authAPI.refresh(payload);
                // ✅ Store user session (TokenService is handled inside `authAPI`)
                //user.value = response.user;
                await this.fetchCurrentUser(true); // ensure fresh data
                return response;
            } catch (error) {
                log.error("❌ Login failed:", error);
                throw error;
            }
        }
    }


    /*
    const saveSession = (newUser: User) => {
        user.value = JSON.stringify(newUser)
    }

    const removeSession = () => {
        user.value = null
        router.push('/auth/sign-in')
    }
    return {
        user,
        login,
        logout,
        saveSession,
        removeSession,
        isAuthenticated
    }
    */
})

/**
 * Reset all stores
 * This is called on logout
 */
function resetAllStores() {
    const dashboardStore = useDashboardStore();
    const userStore = useUserStore();
    const profileStore = useProfileStore();
    const eventStore = useEventsStore();
    const applicationStore = useApplicationsStore();
    const assignmentStore = useAssignmentsStore();
    const ratingStore = useRatingStore();
    const recommendationStore = useRecommendationStore();

    dashboardStore.clearMyData();
    userStore.clearMyData();
    profileStore.clearMyData();
    eventStore.clearMyData();
    applicationStore.clearMyData();
    assignmentStore.clearMyData();
    ratingStore.clearMyData();
    recommendationStore.clearMyData();

    //applicationStore.$reset();
}
